<script>
  export default {
    props: ["roomsData", "showPlatformsToggle", "showPlatforms", "platformsData"],
    components: {
      toggle: () => import("@/components/widgets/ToggleSwitch.vue"),
    },
    data() {
      return {
        heightFather: 0,
        isCollapseOpen: true,
      };
    },
    computed: {
      userPlatformType() {
        return this.$store?.state?.user?.profile?.platformType || [];
      },
      userRole() {
        return this.$store?.state?.user?.profile?.role || "";
      },
      allowDual() {
        const platforms = this.userPlatformType;
        const roles = ["coordinator", "monitor"];

        if (this.userRole === "superadmin") {
          return true;
        }

        if (roles.includes(this.userRole)) {
          return platforms.includes("private");
        }

        return true;
      },
      allowDualFree() {
        const platforms = this.userPlatformType;

        if (this.userRole === "superadmin") {
          return true;
        }

        return platforms.includes("free");
      },
      platformMap() {
        return [
          { name: "LiveJasmin", color: "#D40101", abbreviation: "JS" },
          { name: "Streamate", color: "#21619A", abbreviation: "ST" },
          {
            background: "linear-gradient(117deg, #F37322 16.64%, #FFD67D 83.36%)",
            name: "Chaturbate",
            abbreviation: "CH",
          },
          { name: "Stripchat", color: "#7F19AD", abbreviation: "STR" },
          { name: "ninguna", color: "#ADADAD" },
          { name: "Dual", background: "linear-gradient(90deg, #D40101 0%, #21619A 100%)" },
          {
            name: "Dual Free",
            background: "linear-gradient(90deg, #7F19AD 0%, #F37322 100%)",
            abbreviation: "DF",
          },
        ];
      },

      platformOptions() {
        const pretty = (name = "") => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
        const toValue = (name = "") => name.toLowerCase().replace(/\s|_/g, "");

        const ordenBase = ["LiveJasmin", "Streamate", "Chaturbate", "Stripchat"];

        const opts = this.platformsData
          .map((p) => ({
            text: pretty(p.name),
            value: toValue(p.name),
            abbreviation: this.platformMap.find((item) => item.name === p.name)?.abbreviation,
          }))
          .sort((a, b) => ordenBase.indexOf(a.text) - ordenBase.indexOf(b.text));

        const extraOptions = [];
        if (this.allowDual) {
          extraOptions.push({ text: "Dual", value: "double", abbreviation: "Dual" });
        }
        if (this.allowDualFree) {
          extraOptions.push({ text: "Dual Free", value: "dualfree", abbreviation: "DF" });
        }

        return [...extraOptions, ...opts];
      },

      validateCurrentPlatform() {
        return (platformToFind) => {
          if ("platform" in this.$route.query) {
            return String(this.$route.query.platform).toLowerCase() === String(platformToFind).toLowerCase();
          }
          return false;
        };
      },

      percentages() {
        const clean = (s = "") =>
          String(s)
            .toLowerCase()
            .replace(/\s|_/g, "");

        const isJasmin = (k) => clean(k).includes("jasmin");
        const isStreamate = (k) => clean(k).includes("streamate");
        const isChaturbate = (k) => clean(k).includes("chaturbate");
        const isStripchat = (k) => clean(k).includes("stripchat");

        const vistos = new Set();
        const uniquePlatforms = [];
        for (const p of this.platformsData) {
          const key = clean(p.name);
          if (!vistos.has(key)) {
            vistos.add(key);
            uniquePlatforms.push(p);
          }
        }

        const counters = {};
        uniquePlatforms.forEach((p) => {
          counters[clean(p.name)] = 0;
        });
        if (this.allowDual) {
          counters.dual = 0;
        }
        if (this.allowDualFree) {
          counters.dualfree = 0;
        }
        counters.ninguna = 0;

        let totalBusy = 0;

        this.roomsData.forEach((room) => {
          const model = room?.modelTemp;
          if (!model?._id) return;
          totalBusy++;

          const active = (model.broadcastingPlatforms || [])
            .map((id) => {
              const found = uniquePlatforms.find((p) => p._id === id);
              return found ? clean(found.name) : null;
            })
            .filter(Boolean);

          const set = new Set(active);
          if (set.size === 0) {
            counters.ninguna++;
            return;
          }

          const hasJ = [...set].some(isJasmin);
          const hasS = [...set].some(isStreamate);
          const hasChaturbate = [...set].some(isChaturbate);
          const hasStripchat = [...set].some(isStripchat);
          const hasDual = hasJ && hasS && this.allowDual;
          const hasDualFree = hasChaturbate && hasStripchat && this.allowDualFree;

          if (hasDual) {
            counters.dual++;
          }

          if (hasDualFree) {
            counters.dualfree++;
          }

          set.forEach((k) => {
            const skipDual = hasDual && (isJasmin(k) || isStreamate(k));
            const skipDualFree = hasDualFree && (isChaturbate(k) || isStripchat(k));

            if (skipDual || skipDualFree || counters[k] === undefined) return;

            counters[k]++;
          });
        });

        const displayName = uniquePlatforms.reduce(
          (acc, p) => {
            acc[clean(p.name)] = p.name;
            return acc;
          },
          {
            ...(this.allowDual ? { dual: "Dual" } : {}),
            ...(this.allowDualFree ? { dualfree: "Dual Free" } : {}),
            ninguna: "ninguna",
          }
        );

        const ordenBase = ["livejasmin", "streamate", ...(this.allowDual ? ["dual"] : []), ...(this.allowDualFree ? ["dualfree"] : []), "chaturbate", "stripchat", "ninguna"];

        const pos = Object.fromEntries(ordenBase.map((n, i) => [n, i]));
        const data = Object.entries(counters)
          .map(([key, val]) => ({
            key,
            name: displayName[key] || key,
            total: val,
            percentage: totalBusy === 0 ? 0 : Math.round((val / totalBusy) * 100),
          }))
          .sort((a, b) => (pos[clean(a.name)] ?? Infinity) - (pos[clean(b.name)] ?? Infinity));

        return data;
      },
      user() {
        return this.$userData;
      },
    },
    methods: {
      isDual(p) {
        return this.allowDual && p.key === "dual";
      },
      isDualFree(p) {
        return this.allowDualFree && p.key === "dualfree";
      },
      isNone(p) {
        return p.key === "ninguna";
      },
      isChaturbate(p) {
        return p.name === "Chaturbate";
      },
      platformStyle(displayName) {
        return this.platformMap.find((item) => displayName === item.name);
      },

      getPlatformIconStyle(p) {
        return this.platformStyle(p.name);
      },

      getPlatformBarStyle(p) {
        const style = this.platformStyle(p.name) || {};
        return {
          width: `${p.percentage}%`,
          background: style.background || style.color || "transparent",
          ...style,
        };
      },

      toggleElementCollapse(collapseInfo) {
        const { fatherIdOrClass, fatherHeight, isCollapseToClose, fatherHeightCollapsed } = collapseInfo;
        const fatherElement = document.querySelector(fatherIdOrClass);
        const howManyDecrease = fatherHeight >= 1000 ? 40 : 20;
        const intervalo = setInterval(() => {
          let alto = fatherElement.offsetHeight;

          if (isCollapseToClose) {
            if (alto <= fatherHeightCollapsed) {
              fatherElement.style.height = `${fatherHeightCollapsed}px`;
              clearInterval(intervalo);
            } else {
              fatherElement.style.height = alto - howManyDecrease + "px";
            }
          } else {
            if (alto >= fatherHeight) {
              clearInterval(intervalo);
            } else {
              fatherElement.style.height = alto + howManyDecrease + "px";
            }
          }
        }, 0);
      },

      toggleCollapse() {
        const fatherElement = document.querySelector(".byPlatform");
        if (this.isCollapseOpen) {
          this.heightFather = fatherElement.offsetHeight;
        }

        this.$emit("toggleElementCollapse", {
          fatherIdOrClass: `.byPlatform`,
          fatherHeight: this.heightFather,
          isCollapseToClose: this.isCollapseOpen,
          fatherHeightCollapsed: 60,
          key: "platforms",
        });

        this.isCollapseOpen = !this.isCollapseOpen;
      },

      platformFilter(evt, platform) {
        const isChecked = evt.target.checked;
        const { query } = this.$route;

        if (!isChecked) {
          const newQueries = {};
          Object.entries(query).forEach(([key, value]) => {
            if (key !== "platform") newQueries[key] = value;
          });
          this.$router.replace({ query: newQueries }).catch(() => {});
        }

        this.$emit("showCleanBtn", { flag: isChecked, platform });
      },
    },
  };
</script>

<template>
  <section class="byPlatform">
    <div :class="['byPlatform__content', 'collapse__container', { 'collapse-active': !isCollapseOpen }]">
      <div :class="['byPlatform__header', 'collapse__header', { 'end-elements': isCollapseOpen }]">
        <p class="byPlatform__title">uso de plataformas respecto habitaciones</p>
        <button class="collapse__button" type="button" @click="toggleCollapse">
          <iconic name="chevronUp" />
        </button>
      </div>

      <div class="byPlatform__body collapse__body">
        <div class="byPlatform__body-header">
          <p class="byPlatform__subtitle">Filtros</p>
          <div class="byPlatform__body-toggles">
            <button type="button" :class="['byPlatform__body-toggles--showPlatforms', { checked: showPlatforms }]" @click="showPlatformsToggle" v-if="!user.platformType.includes('free')">
              <iconic name="play" />
              <iconic name="play" />
            </button>

            <toggle
              v-for="(platform, idx) in platformOptions"
              :key="idx"
              @change="platformFilter($event, platform.value)"
              :checked="validateCurrentPlatform(platform.value)"
              :label="platform.abbreviation"
              :changeLabelPosition="true"
            />
          </div>
        </div>

        <div class="byPlatform__body-wrapper">
          <div class="byPlatform__body-platform" v-for="p in percentages" :key="p.key">
            <div class="byPlatform__body-platformHeader">
              <div class="byPlatform__body-platformData">
                <div class="byPlatform__body-platformData--platforms">
                  <div class="byPlatform__body-platformData--none" v-if="isDual(p)">
                    <iconic name="play" class="byPlatform__body-platformData--jasmin" />
                    <iconic :style="{ color: '#21619a', fontSize: '15px' }" name="play" />
                  </div>
                  <div class="byPlatform__body-platformData--dualFree" v-else-if="isDualFree(p)">
                    <div class="byPlatform__shape byPlatform__shape--dual byPlatform__shape--small"></div>
                    <iconic name="play" class="byPlatform__play byPlatform__play--dual byPlatform__play--stripchat" />
                  </div>
                  <div class="byPlatform__shape" v-else-if="isChaturbate(p)"></div>
                  <iconic v-else-if="isNone(p)" name="forbidden" />
                  <iconic v-else name="play" class="byPlatform__play" :style="getPlatformIconStyle(p)" />
                </div>
                <div>
                  <span class="byPlatform__body-platformData--platform">
                    {{ `${p.total} ${p.name}` }}
                  </span>
                </div>
              </div>

              <span class="byPlatform__body-percentage">{{ `${p.percentage}%` }}</span>
            </div>

            <div class="byPlatform__body-percentageBar">
              <div class="byPlatform__body-percentageBar byPlatform__body-percentageBar--fill" :style="getPlatformBarStyle(p)"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
  .byPlatform {
    &__content {
      background: #fff;
      border-radius: 9px;
      transition: opacity 0.3s ease-in-out;
      box-shadow: 0px 1px 4px 0px #00000040;

      &.collapse-active {
        .collapse__body {
          transition: opacity 0.3s ease-in-out;
          height: 0;
          opacity: 0;
        }
        .collapse__header {
          padding: 15px;
        }
      }
    }
    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 15px;
    }
    &__title {
      font-size: 18px;
    }
    &__title,
    &__subtitle {
      font-family: $sec-font;
    }
    &__subtitle {
      display: none;
      font-size: 20px;
    }
    @include desktopWidth {
      &__subtitle {
        display: block;
      }
      &__title {
        font-size: 22px;
      }
    }
    &__shape {
      width: 17px;
      height: 17px;
      background: linear-gradient(153deg, #f37322 4.79%, #ffd67d 81.79%);

      -webkit-mask: url("data:image/svg+xml;utf8,  <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'>    <path d='M20,12 A16,16 0 0 0 12,20 V80 A16,16 0 0 0 20,88 L68,60 Q92,50 68,40 Z' fill='black'/>  </svg>")
        center/100% 100% no-repeat;
      mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><path d='M20,12 A8,8 0 0 0 12,20 V80 A8,8 0 0 0 20,88 L68,60 Q92,50 68,40 Z' fill='black'/></svg>")
        center/100% 100% no-repeat;

      &--small {
        width: 12px;
        height: 12px;
      }

      &--dual {
        width: 15px;
        height: 15px;
      }
    }

    &__play {
      max-width: 13px;
      max-height: 15px;
      position: relative;

      &--stripchat {
        color: #7f19ad;
        width: 17px;
        height: 17px;
      }

      &--dual {
        position: absolute;
        left: 7px;
      }
    }

    &__body {
      @include Flex(column, flex-start, flex-start);
      gap: 20px;
      width: 100%;
      padding: 15px;
      padding-top: 0;
      transition: 0s;
      overflow: hidden;
      &.collapse-active {
        display: none;
      }
      &-header {
        @include Flex(column, flex-start, flex-start);
        gap: 20px;
        width: 100%;
        .toggleSwitch {
          &-switch {
            width: 50px;
            height: 26px;
            &:before {
              width: 17px;
              height: 17px;
            }
          }
          &-checkbox:checked + .toggleSwitch-switch {
            &:before {
              left: 27px;
            }
          }
        }
      }
      &-toggles {
        @include Flex(row, flex-start, center);
        flex-wrap: wrap;
        gap: 20px;
        margin-bottom: 20px;
        width: 100%;
        font-weight: bold;
        &--showPlatforms {
          display: flex;
          border: none;
          background: transparent;
          gap: 5px;
          position: relative;
          .iconic {
            color: #ccc;
            transition: color 0.3s ease-in-out;
            font-size: 20px;
            &:not(:last-of-type) {
              margin-right: -15px;
            }
          }
          &.checked {
            .iconic {
              &:not(:last-of-type) {
                color: #21619a;
                z-index: 10;
              }
              &:last-of-type {
                color: #bd0909;
              }
            }
            &::after {
              content: "";
              display: none;
            }
          }
          &::after {
            content: "";
            position: absolute;
            transition: all 0.3s ease-in-out;
            width: 4px;
            height: 30px;
            left: 12px;
            top: -5px;
            transform: rotate(35deg);
            background: #3a3a3a;
            border: 0.2px solid #fff;
            z-index: 20;
            border-radius: 10px;
          }
        }
      }

      &-wrapper {
        @include Flex(column, space-around, flex-start);
        width: 100%;
        height: 100%;
        gap: 10px;
      }
      &-platform {
        @include Flex(column, flex-start, flex-start);
        width: 100%;
        gap: 5px;
      }
      &-platformHeader {
        @include Flex(row, space-between);
        width: 100%;
        gap: 5px;
        font-family: $sec-font;
      }
      &-platformData {
        position: relative;
        @include Flex(row);
        gap: 10px;
        font-size: 16px;
        &--platforms {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          width: 30px;
          :nth-child(n) {
            z-index: 1;
          }
          .iconic {
            font-size: 15px;
          }
        }
        &--ambas {
          justify-content: flex-start;
          .iconic:not(:last-of-type) {
            position: absolute;
            left: 10px;
          }
          .iconic:last-of-type {
            z-index: 10;
          }
        }

        &--jasmin {
          color: #d50101;
          font-size: 15px;

          z-index: 10;
          position: absolute;
          left: 7px;
        }
        &--platform {
          font-family: $first_font;
          color: #2f2f2f;
          font-weight: 400;
          font-size: 16px;
          text-transform: uppercase;
        }
        &--none {
          display: flex;
        }
        &--dualFree {
          @include Flex(row, center, center);
          gap: 2px;
        }
      }
      &-percentage {
        font-size: 19px;
        color: $primary-color;
      }
      &-percentageBar {
        width: 100%;
        height: 11px;
        background-color: #f0f0f0;
        border-radius: 7px;
        transition: 1s ease-in-out;
        &--fill {
          width: 0;
          max-width: 100%;
          height: 8px;
          background-color: $primary-color;
          border-radius: 7px;
        }
      }

      @include desktopWidth {
        .byPlatform__body {
          &-toggles {
            width: 100%;
          }
        }
        &__title {
          width: 90%;
          text-align: left;
        }
        &-header {
          @include Flex(row, center, flex-start);
        }
        &-toggles {
          @include Flex(row, flex-end, center);
          gap: 14px;
          margin-bottom: 0;
          width: 70%;
        }
      }
    }
  }
</style>
